/**
 * JSON-LD schema builders for TanStack head().scripts entries.
 * Each helper returns a script descriptor ready to spread into head().
 */

const SITE_URL = "https://www.enno-und-jens.de";

function abs(pathOrUrl: string) {
  return pathOrUrl.startsWith("http") ? pathOrUrl : `${SITE_URL}${pathOrUrl}`;
}

function ld(data: unknown) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}

export function breadcrumbSchema(
  items: { name: string; path: string }[],
) {
  return ld({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: abs(item.path),
    })),
  });
}

export function serviceSchema(opts: {
  name: string;
  description: string;
  path: string;
  image?: string;
  serviceType?: string;
  audienceType?: string;
}) {
  return ld({
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: abs(opts.path),
    ...(opts.image ? { image: abs(opts.image) } : {}),
    ...(opts.serviceType ? { serviceType: opts.serviceType } : {}),
    ...(opts.audienceType
      ? { audience: { "@type": "Audience", audienceType: opts.audienceType } }
      : {}),
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#organization`,
      name: "Enno & Jens",
      url: SITE_URL,
    },
    areaServed: [
      { "@type": "Place", name: "Wernigerode" },
      { "@type": "Place", name: "Harz" },
      { "@type": "Place", name: "Sachsen-Anhalt" },
      { "@type": "Country", name: "Deutschland" },
    ],
  });
}

export function courseSchema(opts: {
  name: string;
  description: string;
  path: string;
  image?: string;
  modes?: { name: string; duration: string }[];
}) {
  return ld({
    "@context": "https://schema.org",
    "@type": "Course",
    name: opts.name,
    description: opts.description,
    url: abs(opts.path),
    ...(opts.image ? { image: abs(opts.image) } : {}),
    inLanguage: "de",
    teaches: "KI-Kompetenz nach Artikel 4 EU AI Act",
    provider: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Enno & Jens",
      url: SITE_URL,
    },
    ...(opts.modes && opts.modes.length
      ? {
          hasCourseInstance: opts.modes.map((mode) => ({
            "@type": "CourseInstance",
            name: mode.name,
            courseMode: ["Onsite", "Online"],
            courseWorkload: mode.duration,
            location: { "@type": "Place", name: "Vor Ort im Unternehmen oder online" },
          })),
        }
      : {}),
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      url: abs("/kontakt"),
      description: "Preis auf Anfrage – abhängig von Format, Gruppengröße und Vorbereitung.",
    },
  });
}



export function articleSchema(opts: {
  headline: string;
  description: string;
  path: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  authorTitle?: string;
  articleSection?: string;
  keywords?: string[];
}) {
  return ld({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline,
    description: opts.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": abs(opts.path) },
    url: abs(opts.path),
    ...(opts.image ? { image: [abs(opts.image)] } : {}),
    datePublished: opts.datePublished,
    dateModified: opts.dateModified ?? opts.datePublished,
    author: {
      "@type": "Person",
      name: opts.authorName,
      ...(opts.authorTitle ? { jobTitle: opts.authorTitle } : {}),
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Enno & Jens",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/images/logo-512.webp`,
      },
    },
    ...(opts.articleSection ? { articleSection: opts.articleSection } : {}),
    ...(opts.keywords && opts.keywords.length
      ? { keywords: opts.keywords.join(", ") }
      : {}),
  });
}

export function creativeWorkSchema(opts: {
  name: string;
  description: string;
  path: string;
  image?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  keywords?: string[];
}) {
  return ld({
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: opts.name,
    description: opts.description,
    url: abs(opts.path),
    ...(opts.image ? { image: abs(opts.image) } : {}),
    ...(opts.url ? { sameAs: opts.url } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    ...(opts.keywords && opts.keywords.length
      ? { keywords: opts.keywords.join(", ") }
      : {}),
    creator: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Enno & Jens",
      url: SITE_URL,
    },
  });
}

export function faqSchema(items: { question: string; answer: string }[]) {
  return ld({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  });
}

export function organizationSchema() {
  return ld({
    "@context": "https://schema.org",
    // LocalBusiness als Subtyp mit ausgeben, damit Google die lokalen
    // Signale (geo, Öffnungszeiten, priceRange) derselben Entität zuordnet.
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${SITE_URL}/#organization`,
    name: "Enno & Jens",
    alternateName: "Goldhammer & Kerwel GbR",
    legalName: "Goldhammer & Kerwel GbR",
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo-512.webp`,
    image: [
      `${SITE_URL}/images/hero-bg-1280.webp`,
      `${SITE_URL}/images/team/enrico-480.webp`,
      `${SITE_URL}/images/team/jens-384.webp`,
    ],
    description:
      "Agentur aus Wernigerode im Harz für Webdesign, SEO, GEO, KI-Beratung und Digitalisierung. Persönlich, herstellerunabhängig und DSGVO-konform.",
    email: "hey@enno-und-jens.de",
    telephone: "+49 3943 2636832",
    slogan: "Ihre Agentur aus dem Harz",
    foundingDate: "2022",
    foundingLocation: { "@type": "Place", name: "Wernigerode, Harz" },
    numberOfEmployees: { "@type": "QuantitativeValue", value: 2 },
    knowsAbout: [
      "Webdesign",
      "SEO-Optimierung",
      "GEO (Generative Engine Optimization)",
      "Hosting",
      "Buchungssysteme",
      "Digitalisierungsberatung",
      "KI-Beratung",
      "E-Commerce",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Am Jägerkopf 4",
      postalCode: "38855",
      addressLocality: "Wernigerode",
      addressRegion: "Sachsen-Anhalt",
      addressCountry: "DE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 51.835,
      longitude: 10.787,
    },
    hasMap:
      "https://www.google.com/maps/search/?api=1&query=Am+J%C3%A4gerkopf+4,+38855+Wernigerode",
    priceRange: "€€",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "16:00",
    },
    makesOffer: [
      "Webdesign",
      "SEO-Optimierung",
      "Hosting & Betrieb",
      "Buchungssysteme",
      "Digitalisierungsberatung",
      "Webseitencheck",
    ].map((name) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name },
    })),
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        telephone: "+49 3943 2636832",
        email: "hey@enno-und-jens.de",
        areaServed: "DE",
        availableLanguage: ["de"],
      },
    ],
    areaServed: [
      "Wernigerode",
      "Halberstadt",
      "Quedlinburg",
      "Blankenburg",
      "Harz",
      "Sachsen-Anhalt",
      "Deutschland",
    ],
    founder: [
      {
        "@type": "Person",
        name: "Jens Goldhammer",
        jobTitle: "Co-Founder & Technical Lead",
        sameAs: "https://www.linkedin.com/in/jens-goldhammer/",
        knowsAbout: [
          "Webentwicklung",
          "SEO",
          "Hosting",
          "Digitalisierung",
          "KI-Beratung",
          "Künstliche Intelligenz",
        ],
      },
      {
        "@type": "Person",
        name: "Enrico Kerwel",
        alternateName: "Enno",
        jobTitle: "Co-Founder & Creative Director",
        sameAs: "https://www.linkedin.com/in/enrico-kerwel-140802315/",
        knowsAbout: ["Webdesign", "UI/UX", "Kundenbetreuung", "Projektmanagement"],
      },
    ],
    sameAs: [
      "https://www.linkedin.com/in/enrico-kerwel-140802315/",
      "https://www.linkedin.com/in/jens-goldhammer/",
      "https://www.wikidata.org/wiki/Q140692638",
    ],
  });
}

export function blogSchema(
  posts: {
    title: string;
    slug: string;
    datePublished: string;
    dateModified?: string;
  }[],
) {
  return ld({
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Ratgeber Enno & Jens",
    url: `${SITE_URL}/ratgeber`,
    description:
      "Praxisnaher Ratgeber zu Webdesign, SEO, Performance und Digitalisierung von Enno & Jens aus Wernigerode.",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE_URL}/ratgeber/${p.slug}`,
      datePublished: p.datePublished,
      dateModified: p.dateModified || p.datePublished,
    })),
  });
}

export function serviceListSchema(
  items: { title: string; slug: string; description: string }[],
) {
  return ld({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${SITE_URL}/leistungen#services`,
    name: "Unsere Leistungen",
    description:
      "Digitale Dienstleistungen der Agentur Enno & Jens aus Wernigerode",
    itemListElement: items.map((s, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Service",
        "@id": `${SITE_URL}/leistungen/${s.slug}`,
        name: s.title,
        description: s.description,
        url: `${SITE_URL}/leistungen/${s.slug}`,
        provider: { "@id": `${SITE_URL}/#organization` },
      },
    })),
  });
}


export function partnerListSchema(
  partners: {
    name: string;
    description: string;
    website: string;
    location: string;
  }[],
) {
  return ld({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${SITE_URL}/netzwerk#partners`,
    name: "Netzwerk von Enno & Jens",
    description: "Unser Netzwerk für ganzheitliche Webprojekte",
    itemListElement: partners.map((partner, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Organization",
        name: partner.name,
        description: partner.description,
        url: partner.website,
        address: {
          "@type": "PostalAddress",
          addressLocality: partner.location,
          addressCountry: "DE",
        },
      },
    })),
  });
}

export function websiteSchema() {

  return ld({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Enno & Jens",
    inLanguage: "de-DE",
    publisher: { "@id": `${SITE_URL}/#organization` },
    description:
      "Agentur aus Wernigerode für Webdesign, SEO, GEO, KI-Beratung und Digitalisierung für KMU im Harz und deutschlandweit.",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/ratgeber?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  });
}
